import { put, call, takeEvery, takeLeading, select } from "redux-saga/effects";
import _pick from "lodash/pick";
import _isEmpty from "lodash/isEmpty";
import _get from "lodash/get";

import { safe } from "providers/GeneralProvider/saga";
import {
  getTravelPlaceListRequest,
  getTravelPlaceListSuccess,
  getTravelPlaceListFailure,
  getTravelPlaceDetailRequest,
  getTravelPlaceDetailSuccess,
  getTravelPlaceDetailFailure,
  createTravelPlaceRequest,
  createTravelPlaceSuccess,
  createTravelPlaceFailure,
  updateTravelPlaceRequest,
  updateTravelPlaceSuccess,
  updateTravelPlaceFailure,
  deleteTravelPlaceRequest,
  deleteTravelPlaceSuccess,
  deleteTravelPlaceFailure,
  restoreTravelPlaceRequest,
  restoreTravelPlaceSuccess,
  restoreTravelPlaceFailure,
} from "./slice";
import Helper from "utils/helpers";
import request from "utils/api";

function* getTravelPlaceList({ meta, payload }) {
  try {
    const params = _pick(payload, [
      "page",
      "limit",
      "search",
      "travelType",
      "travelLocation",
      "latitude",
      "longitude",
      "radius",
      "minRating",
      "isDeleted",
    ]);

    // Set defaults
    params.page = params.page || 1;
    params.limit = params.limit || 10;

    // Clean up empty params
    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    const { data } = yield call(request, "api/travel/place", params, {
      method: "GET",
    });

    yield put(getTravelPlaceListSuccess(data, meta));
  } catch (error) {
    yield put(getTravelPlaceListFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể tải danh sách địa điểm du lịch", "error");
  }
}

function* getTravelPlaceDetail({ meta, payload }) {
  try {
    const params = {};

    // Add user location if available for distance calculation
    if (payload.latitude && payload.longitude) {
      params.latitude = payload.latitude;
      params.longitude = payload.longitude;
    }

    const { data } = yield call(
      request,
      `api/travel/place/${payload.id}`,
      params,
      { method: "GET" }
    );

    yield put(getTravelPlaceDetailSuccess(data, meta));
  } catch (error) {
    yield put(getTravelPlaceDetailFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể tải thông tin địa điểm", "error");
  }
}

function* createTravelPlace({ meta, payload }) {
  try {
    const params = _pick(payload.data, [
      "placeName",
      "travelType",
      "travelLocation",
      "phone",
      "website",
      "address",
      "latitude",
      "longitude",
      "detailInformation",
      "journeyDetail",
      "images",
      "thumbnail",
      "workingTimeMethod",
      "workingTime",
    ]);

    // Handle workingTimeMethod and workingHour based on API requirements
    // API accepts workingTimeMethod field to control working hours behavior

    if (params.workingTimeMethod === "custom") {
      // For custom hours, workingHour is REQUIRED
      if (params.workingTime && Array.isArray(params.workingTime)) {
        // Clean up workingHour data - remove any _id fields from all days
        params.workingHour = params.workingTime.map(day => {
          if (!day) {
            // Return default structure if day is null/undefined
            return {
              time: [],
              alwaysClose: false,
              alwaysOpen: false
            };
          }
          const cleanDay = { ...day };
          // Remove _id from day object if exists
          delete cleanDay._id;
          // Clean time array if exists
          if (cleanDay.time && Array.isArray(cleanDay.time)) {
            cleanDay.time = cleanDay.time.map(slot => {
              const { _id, ...cleanSlot } = slot;
              return cleanSlot;
            });
          }
          // Ensure required fields exist
          cleanDay.alwaysClose = cleanDay.alwaysClose || false;
          cleanDay.alwaysOpen = cleanDay.alwaysOpen || false;
          cleanDay.time = cleanDay.time || [];
          return cleanDay;
        });
      } else {
        // If custom but no workingTime provided, create default 7 days
        params.workingHour = Array(7).fill(null).map(() => ({
          time: [],
          alwaysClose: false,
          alwaysOpen: false
        }));
      }
    } else if (params.workingTimeMethod === "always_open") {
      // For always_open, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_open";
    } else if (params.workingTimeMethod === "always_close") {
      // For always_close, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_close";
    } else if (params.workingTimeMethod === "no_time") {
      // For no_time, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "no_time";
    } else {
      // Default to no_time if workingTimeMethod not specified
      params.workingTimeMethod = "no_time";
      delete params.workingHour;
    }

    // Clean up workingTime field - it's only for frontend
    delete params.workingTime;

    // Validate required fields
    if (!params.placeName || !params.travelType || !params.address) {
      throw new Error("Vui lòng điền đầy đủ thông tin bắt buộc");
    }

    // Validate coordinates
    if (params.latitude && params.longitude) {
      const lat = parseFloat(params.latitude);
      const lng = parseFloat(params.longitude);

      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        throw new Error("Tọa độ không hợp lệ");
      }
    }

    // Format working hours if provided
    if (params.workingHour && Array.isArray(params.workingHour)) {
      if (params.workingHour.length !== 7) {
        throw new Error("Giờ làm việc phải có đủ 7 ngày trong tuần");
      }
    }

    const { data } = yield call(request, "api/travel/place", params, {
      method: "POST",
    });

    yield put(createTravelPlaceSuccess(data, meta));
    Helper.toastr("Thành công", "Đã tạo địa điểm du lịch mới", "success");
  } catch (error) {
    yield put(createTravelPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", error.message || "Không thể tạo địa điểm du lịch", "error");
    throw error;
  }
}

function* updateTravelPlace({ meta, payload }) {
  try {
    const { id, data } = payload;

    const params = _pick(data, [
      "placeName",
      "travelType",
      "travelLocation",
      "phone",
      "website",
      "address",
      "latitude",
      "longitude",
      "location",
      "description",
      "detailInformation",
      "journeyDetail",
      "images",
      "thumbnail",
      "workingTimeMethod",
      "workingTime",
    ]);

    // Handle workingTimeMethod and workingHour based on API requirements
    // API accepts workingTimeMethod field to control working hours behavior

    if (params.workingTimeMethod === "custom") {
      // For custom hours, workingHour is REQUIRED
      if (params.workingTime && Array.isArray(params.workingTime)) {
        // Clean up workingHour data - remove any _id fields from all days
        params.workingHour = params.workingTime.map(day => {
          if (!day) {
            // Return default structure if day is null/undefined
            return {
              time: [],
              alwaysClose: false,
              alwaysOpen: false
            };
          }
          const cleanDay = { ...day };
          // Remove _id from day object if exists
          delete cleanDay._id;
          // Clean time array if exists
          if (cleanDay.time && Array.isArray(cleanDay.time)) {
            cleanDay.time = cleanDay.time.map(slot => {
              const { _id, ...cleanSlot } = slot;
              return cleanSlot;
            });
          }
          // Ensure required fields exist
          cleanDay.alwaysClose = cleanDay.alwaysClose || false;
          cleanDay.alwaysOpen = cleanDay.alwaysOpen || false;
          cleanDay.time = cleanDay.time || [];
          return cleanDay;
        });
      } else {
        // If custom but no workingTime provided, create default 7 days
        params.workingHour = Array(7).fill(null).map(() => ({
          time: [],
          alwaysClose: false,
          alwaysOpen: false
        }));
      }
    } else if (params.workingTimeMethod === "always_open") {
      // For always_open, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_open";
    } else if (params.workingTimeMethod === "always_close") {
      // For always_close, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_close";
    } else if (params.workingTimeMethod === "no_time") {
      // For no_time, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "no_time";
    } else {
      // Default to no_time if workingTimeMethod not specified
      params.workingTimeMethod = "no_time";
      delete params.workingHour;
    }

    // Clean up workingTime field - it's only for frontend
    delete params.workingTime;

    const { data: responseData } = yield call(
      request,
      `api/travel/place/${id}`,
      params,
      { method: "PUT" }
    );

    yield put(updateTravelPlaceSuccess(responseData, meta));
    Helper.toastr("Thành công", "Đã cập nhật địa điểm du lịch", "success");

    return responseData;
  } catch (error) {
    yield put(updateTravelPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", error.message || "Không thể cập nhật địa điểm", "error");
    throw error;
  }
}

function* deleteTravelPlace({ meta, payload }) {
  try {
    const { id, _id } = payload;
    const placeId = id || _id; // Support both id and _id

    yield call(
      request,
      `api/travel/place/${placeId}`,
      {},
      { method: "DELETE" }
    );

    yield put(deleteTravelPlaceSuccess({}, meta));
    Helper.toastr("Thành công", "Đã xóa địa điểm du lịch", "success");

    // Refresh list after deletion
    const filters = yield select((state) => state.travelPlace.filters);
    yield put(getTravelPlaceListRequest(filters));
  } catch (error) {
    yield put(deleteTravelPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể xóa địa điểm", "error");
  }
}

function* restoreTravelPlace({ meta, payload }) {
  try {
    const { id, _id } = payload;
    const placeId = id || _id; // Support both id and _id

    const { data } = yield call(
      request,
      `api/travel/place/${placeId}/restore`,
      {},
      { method: "PUT" }
    );

    yield put(restoreTravelPlaceSuccess(data, meta));
    Helper.toastr("Thành công", "Đã khôi phục địa điểm du lịch", "success");

    // Refresh list after restoration
    const filters = yield select((state) => state.travelPlace.filters);
    yield put(getTravelPlaceListRequest(filters));
  } catch (error) {
    yield put(restoreTravelPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể khôi phục địa điểm", "error");
  }
}

function* travelPlaceSaga() {
  yield takeLeading(getTravelPlaceListRequest.type, safe(getTravelPlaceList));
  yield takeEvery(getTravelPlaceDetailRequest.type, safe(getTravelPlaceDetail));
  yield takeEvery(createTravelPlaceRequest.type, safe(createTravelPlace));
  yield takeEvery(updateTravelPlaceRequest.type, safe(updateTravelPlace));
  yield takeEvery(deleteTravelPlaceRequest.type, safe(deleteTravelPlace));
  yield takeEvery(restoreTravelPlaceRequest.type, safe(restoreTravelPlace));
}

export default travelPlaceSaga;